import { createColumnHelper } from '@tanstack/react-table';
import cn from 'classnames';
import { Link } from '#components/atoms/link/Link.tsx';
import { CopyPill } from '#components/atoms/pill/copy-pill/CopyPill.tsx';
import { Timestamp } from '#components/atoms/timestamp/Timestamp.tsx';
import { Tooltip } from '#components/atoms/tooltip/Tooltip.tsx';
import { RoleLink } from '#components/patterns/entity-links/role-link/RoleLink.tsx';
import { Table } from '#components/patterns/table/Table.tsx';
import type { APIKeyListItem } from '#dn-types/api-key.ts';
import { useGuaranteedId } from '#hooks/useGuaranteedId.ts';
import { ExpiresAt } from '../../detail/ExpiresAt.tsx';
import { LastSeenAt } from '../../detail/LastSeenAt.tsx';
import { allPermissions } from '../../form/permissions.ts';
import { APIKeysActionMenu } from './APIKeysActionMenu.tsx';
import styles from './APIKeysList.module.css';

type Props = {
  data: APIKeyListItem[];
  isFetching: boolean;
  className?: string;
  /** Show the role-scope column.  Gated on ROLE_SCOPED_API_KEYS so orgs without the feature don't see it. */
  showRoleScope?: boolean;
};

const columnHelper = createColumnHelper<APIKeyListItem>();

const columns = [
  columnHelper.accessor('name', {
    header: 'Name',
    size: 200,
    cell: (info) => {
      const name = info.getValue();
      const { id } = info.row.original;
      return (
        <Link className="a inline truncate" to={`/settings/api-keys/${id}`}>
          {name}
        </Link>
      );
    },
    meta: {
      truncate: true,
    },
  }),
  columnHelper.accessor('id', {
    header: 'ID',
    size: 160,
    cell: (info) => <CopyPill className="-ml-6" value={info.getValue()} />,
    meta: {
      className: styles.APIKeysList_id,
    },
  }),
  columnHelper.accessor('permissions', {
    header: 'Scopes',
    size: 90,
    cell: function Cell(info) {
      const permissions = info.getValue();
      const descId = useGuaranteedId({ prefix: 'scopes' });
      const filteredPermissions = allPermissions.filter((permission) => permissions?.includes(permission));
      const joinedPermissions = filteredPermissions.join(', ');

      return (
        <span className={cn('center', styles.APIKeysList_scopes)}>
          <Tooltip
            content={filteredPermissions.length ? joinedPermissions : 'No scopes'}
            className={cn('p-sm', styles.APIKeysList_scopesCount)}
            render={<button type="button" aria-describedby={descId} />}
          >
            {filteredPermissions.length}
          </Tooltip>
        </span>
      );
    },
    meta: {
      align: 'center',
      className: styles.APIKeysList_permissions,
    },
  }),
  columnHelper.accessor('roleID', {
    id: 'roleID',
    header: 'Role scope',
    size: 140,
    cell(info) {
      const roleID = info.getValue();
      if (!roleID) {
        return <span className="p-sm">None</span>;
      }
      return <RoleLink roleID={roleID} className="inline truncate" />;
    },
    meta: {
      truncate: true,
      className: styles.APIKeysList_roleScope,
    },
  }),
  columnHelper.accessor('createdAt', {
    header: 'Created',
    size: 240,
    cell(info) {
      return <Timestamp datetime={info.getValue()} />;
    },
    meta: {
      truncate: true,
      className: styles.APIKeysList_createdAt,
    },
  }),
  columnHelper.accessor('expiresAt', {
    header: 'Expires',
    size: 240,
    cell(info) {
      return <ExpiresAt expiresAt={info.getValue()} />;
    },
    meta: {
      truncate: true,
      className: styles.APIKeysList_expiresAt,
    },
  }),
  columnHelper.accessor('lastSeenAt', {
    header: 'Last used',
    size: 140,
    cell(info) {
      const { lastSeenAt, createdAt } = info.row.original;
      return <LastSeenAt lastSeenAt={lastSeenAt} createdAt={createdAt} />;
    },
    meta: {
      className: styles.APIKeysList_lastSeenAt,
    },
  }),
  columnHelper.display({
    id: 'action-menu',
    header: '',
    size: 34,
    cell(info) {
      const { id } = info.row.original;
      return (
        <div className="flex justify-end">
          <APIKeysActionMenu value={id} />
        </div>
      );
    },
  }),
];

export function APIKeysList({ data, isFetching, className, showRoleScope = false }: Props) {
  const visibleColumns = showRoleScope ? columns : columns.filter((column) => column.id !== 'roleID');
  return <Table className={cn({ 'opacity-dim': isFetching }, className)} data={data} columns={visibleColumns} />;
}
